import { AUDIT_BASE_URL } from '../../utils/ApiUrls';

class AdminAuditService {
    // ===== AUDIT PROGRAMS =====
    
    /**
     * Get list of audit programs for organization admin
     * @param {string} accessToken - JWT access token
     * @param {Object} params - Query parameters (search, status, ordering, page, page_size)
     */
    async getAuditPrograms(accessToken, params = {}) {
        try {
            const queryParams = new URLSearchParams();
            
            // Add filters
            if (params.search) queryParams.append('search', params.search);
            if (params.status) queryParams.append('status', params.status);
            if (params.ordering) queryParams.append('ordering', params.ordering);
            
            // Add pagination
            if (params.page) queryParams.append('page', params.page);
            if (params.page_size) queryParams.append('page_size', params.page_size);
            
            const response = await fetch(
                `${AUDIT_BASE_URL}/admin/programs/?${queryParams.toString()}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${accessToken}`
                    }
                }
            );

            const data = await response.json();

            if (!response.ok) {
                return {
                    success: false,
                    status: response.status,
                    error: data.detail || 'Failed to fetch audit programs',
                    errorData: data
                };
            }

            return {
                success: true,
                status: response.status,
                data: data
            };
        } catch (error) {
            return {
                success: false,
                status: 0,
                error: error.message || 'Network error occurred',
                errorData: null
            };
        }
    }

    /**
     * Get audit program details for organization admin
     * @param {string} accessToken - JWT access token
     * @param {number} programId - Audit program ID
     */
    async getAuditProgramDetail(accessToken, programId) {
        try {
            const response = await fetch(
                `${AUDIT_BASE_URL}/admin/programs/${programId}/`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${accessToken}`
                    }
                }
            );

            const data = await response.json();

            if (!response.ok) {
                return {
                    success: false,
                    status: response.status,
                    error: data.detail || 'Failed to fetch audit program details',
                    errorData: data
                };
            }

            return {
                success: true,
                status: response.status,
                data: data
            };
        } catch (error) {
            return {
                success: false,
                status: 0,
                error: error.message || 'Network error occurred',
                errorData: null
            };
        }
    }

    // ===== AUDIT PLANS =====
    
    /**
     * Get list of audit plans for organization admin
     * @param {string} accessToken - JWT access token
     * @param {Object} params - Query parameters (search, status, program, is_published, ordering, page, page_size)
     */
    async getAuditPlans(accessToken, params = {}) {
        try {
            const queryParams = new URLSearchParams();
            
            // Add filters
            if (params.search) queryParams.append('search', params.search);
            if (params.status) queryParams.append('status', params.status);
            if (params.program) queryParams.append('program', params.program);
            if (params.is_published !== undefined) queryParams.append('is_published', params.is_published);
            if (params.ordering) queryParams.append('ordering', params.ordering);
            
            // Add pagination
            if (params.page) queryParams.append('page', params.page);
            if (params.page_size) queryParams.append('page_size', params.page_size);
            
            const response = await fetch(
                `${AUDIT_BASE_URL}/admin/plans/?${queryParams.toString()}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${accessToken}`
                    }
                }
            );

            const data = await response.json();

            if (!response.ok) {
                return {
                    success: false,
                    status: response.status,
                    error: data.detail || 'Failed to fetch audit plans',
                    errorData: data
                };
            }

            return {
                success: true,
                status: response.status,
                data: data
            };
        } catch (error) {
            return {
                success: false,
                status: 0,
                error: error.message || 'Network error occurred',
                errorData: null
            };
        }
    }

    /**
     * Get audit plan details for organization admin
     * @param {string} accessToken - JWT access token
     * @param {number} planId - Audit plan ID
     */
    async getAuditPlanDetail(accessToken, planId) {
        try {
            const response = await fetch(
                `${AUDIT_BASE_URL}/admin/plans/${planId}/`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${accessToken}`
                    }
                }
            );

            const data = await response.json();

            if (!response.ok) {
                return {
                    success: false,
                    status: response.status,
                    error: data.detail || 'Failed to fetch audit plan details',
                    errorData: data
                };
            }

            return {
                success: true,
                status: response.status,
                data: data
            };
        } catch (error) {
            return {
                success: false,
                status: 0,
                error: error.message || 'Network error occurred',
                errorData: null
            };
        }
    }




        // ===== AUDIT SECTIONS =====
    
    /**
     * Get list of audit sections for a specific audit plan (organization admin)
     * @param {string} accessToken - JWT access token
     * @param {number} planId - Audit plan ID
     * @param {Object} params - Query parameters (search, ordering, page, page_size)
     */
    async getAuditSections(accessToken, planId, params = {}) {
        try {
            const queryParams = new URLSearchParams();
            
            // Add filters
            if (params.search) queryParams.append('search', params.search);
            if (params.ordering) queryParams.append('ordering', params.ordering);
            
            // Add pagination
            if (params.page) queryParams.append('page', params.page);
            if (params.page_size) queryParams.append('page_size', params.page_size);
            
            const response = await fetch(
                `${AUDIT_BASE_URL}/admin/plans/${planId}/sections/?${queryParams.toString()}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${accessToken}`
                    }
                }
            );

            const data = await response.json();

            if (!response.ok) {
                return {
                    success: false,
                    status: response.status,
                    error: data.detail || 'Failed to fetch audit sections',
                    errorData: data
                };
            }

            return {
                success: true,
                status: response.status,
                data: data
            };
        } catch (error) {
            return {
                success: false,
                status: 0,
                error: error.message || 'Network error occurred',
                errorData: null
            };
        }
    }

    /**
     * Get audit section details for organization admin
     * @param {string} accessToken - JWT access token
     * @param {number} sectionId - Audit section ID
     */
    async getAuditSectionDetail(accessToken, sectionId) {
        try {
            const response = await fetch(
                `${AUDIT_BASE_URL}/admin/sections/${sectionId}/`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${accessToken}`
                    }
                }
            );

            const data = await response.json();

            if (!response.ok) {
                return {
                    success: false,
                    status: response.status,
                    error: data.detail || 'Failed to fetch audit section details',
                    errorData: data
                };
            }

            return {
                success: true,
                status: response.status,
                data: data
            };
        } catch (error) {
            return {
                success: false,
                status: 0,
                error: error.message || 'Network error occurred',
                errorData: null
            };
        }
    }



    // ===== AUDIT QUESTIONS =====
    
    /**
     * Get list of audit questions for a specific audit section (organization admin)
     * @param {string} accessToken - JWT access token
     * @param {number} sectionId - Audit section ID
     * @param {Object} params - Query parameters (search, status, ordering, page, page_size)
     */
    async getAuditQuestions(accessToken, sectionId, params = {}) {
        try {
            const queryParams = new URLSearchParams();
            
            // Add filters
            if (params.search) queryParams.append('search', params.search);
            if (params.status) queryParams.append('status', params.status);
            if (params.ordering) queryParams.append('ordering', params.ordering);
            
            // Add pagination
            if (params.page) queryParams.append('page', params.page);
            if (params.page_size) queryParams.append('page_size', params.page_size);
            
            const response = await fetch(
                `${AUDIT_BASE_URL}/admin/sections/${sectionId}/questions/?${queryParams.toString()}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${accessToken}`
                    }
                }
            );

            const data = await response.json();

            if (!response.ok) {
                return {
                    success: false,
                    status: response.status,
                    error: data.detail || 'Failed to fetch audit questions',
                    errorData: data
                };
            }

            return {
                success: true,
                status: response.status,
                data: data
            };
        } catch (error) {
            return {
                success: false,
                status: 0,
                error: error.message || 'Network error occurred',
                errorData: null
            };
        }
    }

    /**
     * Get audit question details for organization admin
     * @param {string} accessToken - JWT access token
     * @param {number} questionId - Audit question ID
     */
    async getAuditQuestionDetail(accessToken, questionId) {
        try {
            const response = await fetch(
                `${AUDIT_BASE_URL}/admin/questions/${questionId}/`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${accessToken}`
                    }
                }
            );

            const data = await response.json();

            if (!response.ok) {
                return {
                    success: false,
                    status: response.status,
                    error: data.detail || 'Failed to fetch audit question details',
                    errorData: data
                };
            }

            return {
                success: true,
                status: response.status,
                data: data
            };
        } catch (error) {
            return {
                success: false,
                status: 0,
                error: error.message || 'Network error occurred',
                errorData: null
            };
        }
    }


    
    // ===== RECOMMENDATION ACTIONS =====
    
    /**
     * Get list of recommendation actions for the organization (organization admin)
     * @param {string} accessToken - JWT access token
     * @param {Object} params - Query parameters (search, status, owner, ordering, page, page_size)
     */
    async getRecommendationActions(accessToken, params = {}) {
        try {
            const queryParams = new URLSearchParams();
            
            // Add filters
            if (params.search) queryParams.append('search', params.search);
            if (params.status) queryParams.append('status', params.status);
            if (params.owner) queryParams.append('owner', params.owner);
            if (params.ordering) queryParams.append('ordering', params.ordering);
            
            // Add pagination
            if (params.page) queryParams.append('page', params.page);
            if (params.page_size) queryParams.append('page_size', params.page_size);
            
            const response = await fetch(
                `${AUDIT_BASE_URL}/admin/recommendation-actions/?${queryParams.toString()}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${accessToken}`
                    }
                }
            );

            const data = await response.json();

            if (!response.ok) {
                return {
                    success: false,
                    status: response.status,
                    error: data.detail || 'Failed to fetch recommendation actions',
                    errorData: data
                };
            }

            return {
                success: true,
                status: response.status,
                data: data
            };
        } catch (error) {
            return {
                success: false,
                status: 0,
                error: error.message || 'Network error occurred',
                errorData: null
            };
        }
    }

    /**
     * Get recommendation action details for organization admin
     * @param {string} accessToken - JWT access token
     * @param {number} actionId - Recommendation action ID
     */
    async getRecommendationActionDetail(accessToken, actionId) {
        try {
            const response = await fetch(
                `${AUDIT_BASE_URL}/admin/recommendation-actions/${actionId}/`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${accessToken}`
                    }
                }
            );

            const data = await response.json();

            if (!response.ok) {
                return {
                    success: false,
                    status: response.status,
                    error: data.detail || 'Failed to fetch recommendation action details',
                    errorData: data
                };
            }

            return {
                success: true,
                status: response.status,
                data: data
            };
        } catch (error) {
            return {
                success: false,
                status: 0,
                error: error.message || 'Network error occurred',
                errorData: null
            };
        }
    }

    /**
     * Download an audit plan attachment
     * @param {string} accessToken - JWT access token
     * @param {number} attachmentId - The attachment ID to download
     */
    async downloadAttachment(accessToken, attachmentId) {
        try {
            const response = await fetch(`${AUDIT_BASE_URL}/plan-attachments/${attachmentId}/download/`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
            });

            if (!response.ok) {
                const errorData = await response.json();
                return {
                    success: false,
                    error: errorData.detail || 'Failed to download attachment'
                };
            }

            // Get the filename from Content-Disposition header
            const contentDisposition = response.headers.get('Content-Disposition');
            let filename = `attachment_${attachmentId}`;
            
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
                if (filenameMatch && filenameMatch[1]) {
                    filename = filenameMatch[1];
                }
            }

            // Get the blob
            const blob = await response.blob();
            
            // Create a download link and trigger it
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            
            // Cleanup
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            return { success: true };
        } catch (error) {
            console.error('Error downloading attachment:', error);
            return {
                success: false,
                error: error.message || 'An error occurred while downloading the attachment'
            };
        }
    }

}

const adminAuditService = new AdminAuditService();
export default adminAuditService;
